import React, { useMemo } from 'react';
import type { ClassNameProp } from '../common-types';
import { Select } from '../form';

export type StudyTimeRangeComboProps = ClassNameProp & {
  availableRange: number[];
  selectedRange: Date | undefined;
  labelTitle: string;
  labelDescription: string;
  labelCombo: string;
  labelNoRange: string;
  labelFctRange: (rangeNr: number) => string;
  readOnly?: boolean;
  isTimeRangePast: boolean;
  onChange?(selectedRange: Date | undefined): void;
};

export function StudyTimeRangeCombo({
  className = '',
  availableRange,
  selectedRange,
  onChange,
  labelTitle,
  labelDescription,
  labelCombo,
  labelNoRange,
  labelFctRange,
  readOnly,
  isTimeRangePast,
  ...props
}: Readonly<StudyTimeRangeComboProps>): JSX.Element {
  const dateRangeOffset = isTimeRangePast ? -1 : 1;
  const emptyItem = useMemo(
    () => ({
      value: '-',
      label: labelNoRange,
      date: undefined
    }),
    [labelNoRange]
  );

  const items: TimeRangeType[] = useMemo(() => {
    return [
      emptyItem,
      ...availableRange.map(r => ({
        value: `${r}`,
        label: labelFctRange(r),
        date: getDateFromRangeNr(r, dateRangeOffset) ?? undefined
      }))
    ];
  }, [emptyItem, availableRange, labelFctRange, dateRangeOffset]);

  const selectedValue = useMemo(() => {
    let minDiff = Infinity;
    let minDiffItem: TimeRangeType = emptyItem;
    for (const item of items) {
      const itemDiff = getDateDiff(item.date, selectedRange);
      if (itemDiff < minDiff) {
        minDiff = itemDiff;
        minDiffItem = item;
      }
    }
    return minDiffItem;
  }, [items, emptyItem, selectedRange]);

  return (
    <div
      {...props}
      className={`ehs-study-time-range-combo ${className}`.trim()}
    >
      <h3 className="ehs-study-time-range-combo-title sh-font font-18">
        {labelTitle}
      </h3>
      <p className="ehs-study-time-range-combo-description font-16-24">
        {labelDescription}
      </p>
      {readOnly ? (
        <p className="ehs-study-time-range-combo-value">
          {selectedRange ? selectedRange.getFullYear() : labelNoRange}
        </p>
      ) : (
        <Select
          className="ehs-study-time-range-combo-combo"
          variant="single"
          items={items}
          itemValueProp="value"
          itemLabelProp="label"
          label={labelCombo}
          value={selectedValue}
          hideClearIcon={true}
          onChange={(selected: TimeRangeType | null) => {
            if (onChange) {
              onChange(selected?.date);
            }
          }}
        />
      )}
    </div>
  );
}

interface TimeRangeType {
  value: string;
  label: string;
  date: Date | undefined;
}

function getDateFromRangeNr(
  rangeNr: number | undefined,
  offset: number
): Date | undefined {
  if (!rangeNr) {
    return undefined;
  }
  const date = new Date();
  date.setFullYear(date.getFullYear() + rangeNr * offset);
  return date;
}

function getDateDiff(date1: Date | undefined, date2: Date | undefined): number {
  if (!date1 || !date2) {
    return Infinity;
  }
  return Math.abs(date1.getTime() - date2.getTime());
}
